import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useReducer
} from 'react';
import * as THREE from 'three';
import { invalidate, useThree } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import PCNode from './MineModel/Nodes/PCNode';
import InstancedNodeMesh from './MineModel/Meshes/InstancedNodeMesh';
import NodeOverlays from './MineModel/Meshes/NodeOverlays';
import MergedLineMesh from './MineModel/Meshes/MergedLineMesh';
import StopeShapeRenderer, { StopeShapeInfo } from './MineModel/Meshes/StopeShapeRenderer';
import { getStopeDateInterval, stopeIntersectsDateRange } from './MineModel/Meshes/stopeShapeDateRange';
import { ANIMATION_STEP_DAYS } from './MineModel/Meshes/stopePourTimeline';
import ImportedSolidMesh from './MineModel/Meshes/ImportedSolidMesh';
import TunnelProfileMesh from './MineModel/Meshes/TunnelProfileMesh';
import ClippingBoxHandles from './MineModel/Meshes/ClippingBoxHandles';
import BulkImportDraggableNode from './MineModel/Meshes/BulkImportDraggableNode';
import BulkImportProjectionLine from './MineModel/Meshes/BulkImportProjectionLine';
import BulkImportSpoolClickMarker from './Scenes/BulkImportSolidsScene/BulkImportSpoolClickMarker';
import StopeFillContent from './MineModel/Meshes/StopeFillContent';
import { BULK_IMPORT_GHOST_NODE_ID } from './Scenes/BulkImportSolidsScene/bulkImportGhost';
import { Model } from './hooks/useModelState';
import RoutesMesh from './MineModel/Meshes/RoutesMesh';
import StopeMesh from './MineModel/Meshes/StopeMesh';
import PCSpool from './MineModel/Spools/PCSpool';
import { visibleLines, visibleNetwork } from './MineModel/levelAreaFilter';
import PCRoute from './MineModel/Route/PCRoute';
import { cadToThreeJsPosition, cadToThreeJsPositionWithOffset } from './Scenes/scene-utils';
import { IntersectionData } from './MineModel/intersectionTypes';
import { returnShortestRoute } from './MineModel/Route/utils';
import { ClippingBounds } from './MineModel/clippingBounds';
import { focusCameraOnPoint } from './MineModel/cameraFraming';
import * as GConstants from './MineModel/GConstants';
import { getMeasureState, subscribeToMeasureState } from './Scenes/BulkEditScene/Toolbar/measureState';
import { buildE2ECanvasHook, isE2ECanvasHookEnabled } from './e2eCanvasHook';

const buildClippingPlanes = (bounds: ClippingBounds, scale: number, offset: THREE.Vector3): THREE.Plane[] => [
  new THREE.Plane(new THREE.Vector3(1, 0, 0), -(-bounds.maxX + offset.x) * scale),
  new THREE.Plane(new THREE.Vector3(-1, 0, 0), (-bounds.minX + offset.x) * scale),
  new THREE.Plane(new THREE.Vector3(0, 0, 1), -(bounds.minY + offset.z) * scale),
  new THREE.Plane(new THREE.Vector3(0, 0, -1), (bounds.maxY + offset.z) * scale),
  new THREE.Plane(new THREE.Vector3(0, 1, 0), -(bounds.minZ + offset.y) * scale),
  new THREE.Plane(new THREE.Vector3(0, -1, 0), (bounds.maxZ + offset.y) * scale),
];

interface ModelRendererProps {
  model: Model;
  onNodeHover: (node: PCNode | null) => void;
  onNodeSelect: (node: PCNode, stopeId?: string) => void;
  onPlantIconSelect?: (node: PCNode) => void;
  onNodeFocus: (node: PCNode, worldPosition?: THREE.Vector3) => void;
  onSpoolFocus: (spool: PCSpool, worldPosition?: THREE.Vector3) => void;
  onSpoolSelect: (spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => void;
  onStopeShapeSelect?: (stopeId: string) => void;
  onRouteFocus?: (route: PCRoute, worldPosition?: THREE.Vector3) => void;
  onIntersections?: (intersections: IntersectionData[]) => void;
  onModelTransform?: (offset: THREE.Vector3, scale: number) => void;
}

const valuesOf = <T, >(obj: Record<string, T> | undefined | null): T[] => {
  if (obj && typeof obj === 'object') {
    return Object.values(obj);
  }
  return [];
};

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

const hasValidCoords = (n: PCNode | undefined | null) => !!n && !!n.coords
  && isFiniteNumber((n as any).coords?.x)
  && isFiniteNumber((n as any).coords?.y)
  && isFiniteNumber((n as any).coords?.z);

const stopeKey = (prefer?: string | number, prefix = 'k', ix = 0) => (prefer != null ? `${prefix}_${prefer}` : `${prefix}_${ix}`);

interface MeasureLineProps {
  model: Model;
  offset: THREE.Vector3;
  scale: number;
  aNodeId: string;
  bNodeId: string;
}

const MeasureLine = ({ model, offset, scale, aNodeId, bNodeId }: MeasureLineProps) => {
  const nodeA = model.nodes[aNodeId];
  const nodeB = model.nodes[bNodeId];

  const lineObject = useMemo(() => {
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(),
      new THREE.Vector3(),
    ]);
    const material = new THREE.LineBasicMaterial({
      color: 0xffcc00,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    return new THREE.Line(geometry, material);
  }, []);

  useEffect(() => () => {
    lineObject.geometry.dispose();
    (lineObject.material as THREE.Material).dispose();
  }, [lineObject]);

  useEffect(() => {
    if (!nodeA || !nodeB) return;
    const [ax, ay, az] = cadToThreeJsPositionWithOffset(nodeA.coords, offset, scale);
    const [bx, by, bz] = cadToThreeJsPositionWithOffset(nodeB.coords, offset, scale);

    const positions = lineObject.geometry.attributes.position as THREE.BufferAttribute;
    positions.setXYZ(0, ax, ay, az);
    positions.setXYZ(1, bx, by, bz);
    positions.needsUpdate = true;
    lineObject.geometry.computeBoundingSphere();
    invalidate();
  }, [nodeA, nodeB, offset, scale, lineObject]);

  if (!nodeA || !nodeB) return null;

  return <primitive object={lineObject} />;
};

interface MeasureLabelMeshProps {
  position: { x: number; y: number; z: number };
  text: string;
  referenceLength: number;
  offset: THREE.Vector3;
  scale: number;
}

const MIN_LABEL_FONT_SIZE = 1.5;
const LABEL_FONT_SCALE_FACTOR = 0.06;

const MeasureLabelMesh = ({ position, text, referenceLength, offset, scale }: MeasureLabelMeshProps) => {
  const [wx, wy, wz] = cadToThreeJsPositionWithOffset(position, offset, scale);
  const fontSize = Math.max(referenceLength * scale * LABEL_FONT_SCALE_FACTOR, MIN_LABEL_FONT_SIZE);

  return (
    <Billboard
      position={[wx, wy, wz]}
      follow
      lockX={false}
      lockY={false}
      lockZ={false}
    >
      <Text
        fontSize={fontSize}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={fontSize * 0.06}
        outlineColor="#000000"
      >
        {text}
      </Text>
    </Billboard>
  );
};

const ModelRenderer = ({
  model,
  onNodeHover,
  onNodeSelect,
  onPlantIconSelect,
  onNodeFocus,
  onSpoolFocus,
  onSpoolSelect,
  onStopeShapeSelect,
  onRouteFocus,
  onIntersections,
  onModelTransform,
}: ModelRendererProps) => {
  const [hoveredNode, setHoveredNode] = useState<PCNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<PCNode | null>(null);
  const [hoveredStopeShapeId, setHoveredStopeShapeId] = useState<string | null>(null);
  const [, forceMeasureUpdate] = useReducer((x: number) => x + 1, 0);
  const { showStopeShapes } = model;
  const { selectedNodeIds: measureSelectedNodeIds, measureLineEndpoints, routeHighlightSpoolIds, measureLabel } = getMeasureState();

  const measureNodeColours = useMemo(() => {
    const overrides: Record<string, string> = {};
    if (measureSelectedNodeIds[0]) overrides[measureSelectedNodeIds[0]] = GConstants.GREEN;
    if (measureSelectedNodeIds[1]) overrides[measureSelectedNodeIds[1]] = GConstants.RED;
    return overrides;
  }, [measureSelectedNodeIds]);

  const measureSpoolColours = useMemo(
    () => Object.fromEntries(routeHighlightSpoolIds.map((id) => [id, GConstants.PC_BLUE])),
    [routeHighlightSpoolIds],
  );

  const allNodesRef = useRef<PCNode[]>([]);
  const nodes: PCNode[] = useMemo(() => {
    const raw = valuesOf<PCNode>(model.nodes || {});
    return raw.filter((n) => !!n && hasValidCoords(n) && n.id !== BULK_IMPORT_GHOST_NODE_ID);
  }, [model.nodes]);

  const nodesForPosition = useMemo(() => {
    const currentNodes = nodes;
    const stableNodes = allNodesRef.current;
    if (stableNodes.length === 0 && currentNodes.length > 0) {
      allNodesRef.current = currentNodes;
      return currentNodes;
    }
    return stableNodes.length > 0 ? stableNodes : currentNodes;
  }, [nodes]);

  const allSpools = useMemo(() => valuesOf(model.spools || {}).filter(Boolean), [model.spools]);

  const lines = useMemo(
    () => visibleLines(allSpools, model),
    [allSpools, model.showInactiveSpools, model.levelAreaFilter, model.spoolDateFilter],
  );
  const { spools, hiddenStopeNodeIds } = lines;

  const allStopes = useMemo(() => Object.values(model.stopes ?? {}).flat(), [model.stopes]);

  // Drawn and picked only; `nodes` keeps every node so filtering never moves the camera framing.
  const { stopes, nodes: drawnNodes } = useMemo(
    () => visibleNetwork(nodes, allSpools, allStopes, model, lines),
    [nodes, allSpools, allStopes, model.levelAreaFilter, lines],
  );

  const stopesWithGeometry: StopeShapeInfo[] = useMemo(
    () => {
      const overviewByStopeId = new Map(
        (model.mineOverview ?? []).map((summary) => [summary.stopeIdentifier.stopeId, summary]),
      );

      return allStopes
        .filter((s: any) => s?.data?.stopeGeometryFile?.path)
        .filter((s) => !hiddenStopeNodeIds.has(s.originNode?.id ?? ''))
        .filter((s: any) => stopeIntersectsDateRange(s?.data, model.stopeShapeDateRange))
        .map((s: any) => {
          const stopeId = s.data.stopeId as string;
          const overview = overviewByStopeId.get(stopeId);
          const originNode = s.originNode?.id ? model.nodes?.[s.originNode.id] ?? s.originNode : s.originNode;
          return {
            stopeId,
            stopeName: s.data.stopeName,
            geometryPath: s.data.stopeGeometryFile.path as string,
            originCoords: originNode?.coords,
            dateInterval: getStopeDateInterval(s.data),
            volume: s.data.volume,
            startDate: s.data.startDate,
            endDate: s.data.endDate,
            pours: s.data.pour ?? [],
            totalCount: overview?.totalCount ?? 0,
            plannedCount: overview?.plannedCount ?? 0,
            completedCount: overview?.completedCount ?? 0,
            plannedVolume: overview?.plannedVolume ?? 0,
            completedVolume: overview?.completedVolume ?? 0,
            isComplete: overview?.isComplete ?? false,
          };
        });
    },
    [allStopes, model.mineOverview, model.nodes, model.stopeShapeDateRange, hiddenStopeNodeIds],
  );

  const pitPressures = useMemo((): Record<string, number> => {
    if (!model.recipeState) return {};
    const selectedRoute = model.getSelectedRoute();
    const hasRoutes = model.routes && Object.keys(model.routes).length > 0;
    const route = selectedRoute || (hasRoutes ? returnShortestRoute(model.routes!) : undefined);
    if (!route?.routePoints) return {};
    const lookup: Record<string, number> = {};
    route.routePoints.forEach((rp) => {
      if (rp.point?.isPit && typeof rp.pressure === 'number') {
        lookup[rp.point.id] = rp.pressure;
      }
    });
    return lookup;
  }, [model.recipeState, model.routes]);

  const handleHover = useCallback((node: PCNode | null) => {
    setHoveredNode(node);
    onNodeHover(node);
  }, [onNodeHover]);

  const handleSelect = useCallback((node: PCNode, stopeId?: string) => {
    setSelectedNode(node);
    onNodeSelect(node, stopeId);
  }, [onNodeSelect]);

  const handlePlantSelect = useCallback((node: PCNode) => {
    onPlantIconSelect?.(node);
  }, [onPlantIconSelect]);

  const handleSpoolSelect = useCallback((spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => {
    onSpoolSelect(spool, clickPoint);
  }, [onSpoolSelect]);

  const handleStopeShapeHoverChange = useCallback((stopeId: string, isHovered: boolean) => {
    setHoveredStopeShapeId((current) => {
      if (isHovered) return stopeId;
      return current === stopeId ? null : current;
    });
  }, []);

  const stableCenterRef = useRef<THREE.Vector3 | null>(null);
  const stableScaleRef = useRef<number>(1);
  const prevProjectIdRef = useRef<string | undefined>(undefined);
  const nodesHashRef = useRef<string>('');

  const nodesHash = useMemo(
    () => nodesForPosition.map((n) => (n as any)?.id).sort().join(','),
    [nodesForPosition],
  );

  const calculateScaleAndOffset = useMemo(() => {
    if (nodesForPosition.length === 0) return { offset: new THREE.Vector3(0, 0, 0), scale: 1 };

    const points = nodesForPosition
      .filter(hasValidCoords)
      .map((n) => cadToThreeJsPosition(n.coords));

    if (points.length === 0) return { offset: new THREE.Vector3(0, 0, 0), scale: 1 };

    const box = new THREE.Box3().setFromPoints(points);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);

    const targetMaxDimension = 100;
    const scale = maxDimension > targetMaxDimension ? targetMaxDimension / maxDimension : 1;

    return { offset: center.negate(), scale };
  }, [nodesForPosition]);

  useEffect(() => {
    if (nodesForPosition.length > 0 && (nodesHash !== nodesHashRef.current || !stableCenterRef.current)) {
      nodesHashRef.current = nodesHash;
      stableCenterRef.current = calculateScaleAndOffset.offset;
      stableScaleRef.current = calculateScaleAndOffset.scale;
    }
  }, [nodesForPosition.length, nodesHash, nodesForPosition, calculateScaleAndOffset]);

  useEffect(() => {
    const currentProjectId = model.project?.entityId;
    if (prevProjectIdRef.current !== undefined && prevProjectIdRef.current !== currentProjectId) {
      allNodesRef.current = [];
      stableCenterRef.current = null;
      stableScaleRef.current = 1;
      nodesHashRef.current = '';
    }
    prevProjectIdRef.current = currentProjectId;
  }, [model.project?.entityId]);

  const groupPosition = useMemo(() => {
    if (stableCenterRef.current) {
      return stableCenterRef.current;
    }
    return calculateScaleAndOffset.offset;
  }, [nodesHash, calculateScaleAndOffset.offset]);

  const groupScale = useMemo(() => stableScaleRef.current, [nodesHash]);

  useEffect(() => {
    if (onModelTransform && groupPosition && groupScale) {
      onModelTransform(groupPosition, groupScale);
    }
  }, [onModelTransform, groupPosition, groupScale]);

  const { gl, camera, controls } = useThree();
  useEffect(() => {
    if (model.showClippingBounds && model.clippingBounds) {
      gl.clippingPlanes = buildClippingPlanes(model.clippingBounds, groupScale, groupPosition);
    } else {
      gl.clippingPlanes = [];
    }
    invalidate();
    return () => { gl.clippingPlanes = []; };
  }, [gl, model.showClippingBounds, model.clippingBounds, groupScale, groupPosition]);

  const importedSolidsList = useMemo(
    () => Object.values(model.importedSolids ?? {}),
    [model.importedSolids],
  );
  const { tunnelProfile } = model;
  const visibleTunnelProfile = tunnelProfile?.visible ? tunnelProfile : null;

  const lastFramedSolidRef = useRef<string | null>(null);
  useEffect(() => {
    const latest = importedSolidsList[importedSolidsList.length - 1];
    if (!latest) {
      lastFramedSolidRef.current = null;
      return;
    }
    if (lastFramedSolidRef.current === latest.guid) return;
    if (!camera) return;

    const bb = latest.boundingBox;
    if (!bb) return;

    lastFramedSolidRef.current = latest.guid;

    const [wx, wy, wz] = cadToThreeJsPositionWithOffset(
      { x: bb.centerX, y: bb.centerY, z: bb.centerZ },
      groupPosition,
      groupScale,
    );
    const worldCenter = new THREE.Vector3(wx, wy, wz);

    const cadSize = Math.max(bb.maxX - bb.minX, bb.maxY - bb.minY, bb.maxZ - bb.minZ);
    const worldRadius = (cadSize / 2) * groupScale;

    focusCameraOnPoint(controls as any, camera, worldCenter, { radius: worldRadius });
    invalidate();
  }, [importedSolidsList, camera, controls, groupPosition, groupScale]);

  const lastFramedTunnelProfileRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tunnelProfile) {
      lastFramedTunnelProfileRef.current = null;
      return;
    }
    if (lastFramedTunnelProfileRef.current === tunnelProfile.guid) return;
    if (!camera) return;

    const bb = tunnelProfile.boundingBox;
    if (!bb) return;

    lastFramedTunnelProfileRef.current = tunnelProfile.guid;

    const [wx, wy, wz] = cadToThreeJsPositionWithOffset(
      { x: bb.centerX, y: bb.centerY, z: bb.centerZ },
      groupPosition,
      groupScale,
    );
    const worldCenter = new THREE.Vector3(wx, wy, wz);

    const cadSize = Math.max(bb.maxX - bb.minX, bb.maxY - bb.minY, bb.maxZ - bb.minZ);
    const worldRadius = (cadSize / 2) * groupScale;

    focusCameraOnPoint(controls as any, camera, worldCenter, { radius: worldRadius });
    invalidate();
  }, [tunnelProfile, camera, controls, groupPosition, groupScale]);

  const clippingBox = useMemo(() => {
    if (!model.clippingBounds) return null;
    const b = model.clippingBounds;
    return new THREE.Box3(
      new THREE.Vector3((-b.maxX + groupPosition.x) * groupScale, (b.minZ + groupPosition.y) * groupScale, (b.minY + groupPosition.z) * groupScale),
      new THREE.Vector3((-b.minX + groupPosition.x) * groupScale, (b.maxZ + groupPosition.y) * groupScale, (b.maxY + groupPosition.z) * groupScale),
    );
  }, [model.clippingBounds, groupPosition, groupScale]);

  const stopeFillActive = !!model.stopeFillState;
  const previewSolidsVisible = importedSolidsList.length > 0 || !!visibleTunnelProfile;

  useEffect(() => subscribeToMeasureState(() => forceMeasureUpdate()), []);

  useEffect(() => {
    if (!isE2ECanvasHookEnabled) return undefined;
    window.bfpE2ECanvas = buildE2ECanvasHook(model, { onNodeSelect: handleSelect, onSpoolSelect: handleSpoolSelect, onIntersections });
    return () => { delete window.bfpE2ECanvas; };
  }, [model, handleSelect, handleSpoolSelect, onIntersections]);

  return (
    <group>
      {/* Main model branch — hidden when the stope fill scene is active,
          which provides its own isolated view of just the stope + pours. */}
      {!stopeFillActive && (
        <>
          {/* Render Lines */}
          <MergedLineMesh
            spools={spools}
            onFocus={onSpoolFocus}
            onSelect={handleSpoolSelect}
            onIntersections={onIntersections}
            model={model}
            offset={groupPosition}
            scale={groupScale}
            colourOverrides={measureSpoolColours}
            backgroundColour={getMeasureState().backgroundColour}
          />

          {/* Render All Routes */}
          <RoutesMesh model={model} offset={groupPosition} scale={groupScale} onRouteFocus={onRouteFocus} />

          {/* Render Nodes */}
          <InstancedNodeMesh
            nodes={drawnNodes}
            hoveredNodeId={hoveredNode?.id ?? null}
            onHover={handleHover}
            onSelect={handleSelect}
            onFocus={onNodeFocus}
            onIntersections={onIntersections}
            model={model}
            offset={groupPosition}
            scale={groupScale}
            colourOverrides={measureNodeColours}
          />

          {/* Measure line between selected nodes */}
          {measureLineEndpoints && (
            <MeasureLine
              model={model}
              offset={groupPosition}
              scale={groupScale}
              aNodeId={measureLineEndpoints.aNodeId}
              bNodeId={measureLineEndpoints.bNodeId}
            />
          )}

          {/* In-scene measurement label */}
          {measureLabel && (
            <MeasureLabelMesh
              position={measureLabel.position}
              text={measureLabel.text}
              referenceLength={measureLabel.referenceLength}
              offset={groupPosition}
              scale={groupScale}
            />
          )}

          {/* Node Overlays (sprites, labels, arrows) */}
          <NodeOverlays
            nodes={drawnNodes}
            hoveredNodeId={hoveredNode?.id ?? null}
            model={model}
            offset={groupPosition}
            scale={groupScale}
            pitPressures={pitPressures}
            onPlantIconSelect={onPlantIconSelect ? handlePlantSelect : undefined}
            onSelect={handleSelect}
            onSpoolSelect={handleSpoolSelect}
          />

          {/* Render Stopes */}
          {stopes.map((stope: any, ix: number) => {
            const originNode = stope?.originNode as PCNode | undefined;
            if (!originNode) return null;
            const nid = originNode.id;
            const node = model.nodes[nid] ?? originNode;
            return (
              <StopeMesh
                key={stopeKey(stope?.data?.stopeId ?? nid, 'stope', ix)}
                node={node}
                stopeId={stope?.data?.stopeId}
                isSelected={selectedNode?.id === nid}
                isHovered={hoveredNode?.id === nid}
                onHover={handleHover}
                onSelect={handleSelect}
                onFocus={onNodeFocus}
                model={model}
                offset={groupPosition}
                scale={groupScale}
              />
            );
          })}

          {/* Render Stope Shapes (imported OBJ geometries) */}
          {showStopeShapes && stopesWithGeometry.length > 0 && (
            <StopeShapeRenderer
              stopes={stopesWithGeometry}
              offset={groupPosition}
              scale={groupScale}
              timelineDay={model.stopeShapeTimelineDay}
              legendConfig={model.stopeLegendConfig}
              opacityScale={model.stopeShapeOpacity}
              pourData={model.stopePourData}
              stepDays={ANIMATION_STEP_DAYS[model.stopeShapeAnimationStep]}
              hoveredStopeId={hoveredStopeShapeId}
              onHoverChange={handleStopeShapeHoverChange}
              onSelect={onStopeShapeSelect}
            />
          )}

          {/* Render Imported Solids (bulk import preview geometries) */}
          {previewSolidsVisible && (
            <>
              <directionalLight position={[10, 20, 10]} intensity={1.2} />
              <directionalLight position={[-10, 10, -10]} intensity={0.8} />
            </>
          )}
          {visibleTunnelProfile && (
            <TunnelProfileMesh
              key={visibleTunnelProfile.guid}
              fileData={visibleTunnelProfile.fileData}
              guid={visibleTunnelProfile.guid}
              groupPosition={groupPosition}
              groupScale={groupScale}
              color={0x6bd0ff}
              opacity={visibleTunnelProfile.opacity}
              sourceName={visibleTunnelProfile.sourceName ?? visibleTunnelProfile.fileName}
            />
          )}
          {importedSolidsList.map((solid) => (
            <ImportedSolidMesh
              key={solid.guid}
              objData={solid.objData}
              guid={solid.guid}
              groupPosition={groupPosition}
              groupScale={groupScale}
              color={solid.highlightColor}
            />
          ))}

          {/* Clipping bounds visualisation */}
          {model.showClippingBounds && clippingBox && (
            <>
              <box3Helper args={[clippingBox, new THREE.Color('#ffaa00')]} />
              {model.clippingBounds && (
                <ClippingBoxHandles
                  model={model}
                  bounds={model.clippingBounds}
                  modelOffset={groupPosition}
                  modelScale={groupScale}
                />
              )}
            </>
          )}

          {/* Bulk import projected ghost node + projection line (rendered when ghost exists in model.nodes) */}
          {model.nodes[BULK_IMPORT_GHOST_NODE_ID] && (
            <>
              <BulkImportDraggableNode
                model={model}
                offset={groupPosition}
                scale={groupScale}
              />
              <BulkImportProjectionLine
                model={model}
                offset={groupPosition}
                scale={groupScale}
              />
            </>
          )}

          {/* Bulk import spool click marker (subscribes to scene state internally) */}
          <BulkImportSpoolClickMarker offset={groupPosition} scale={groupScale} />
        </>
      )}

      {/* Stope fill scene — isolated stope + pours view */}
      {stopeFillActive && model.stopeFillState && (
        <StopeFillContent stopeFillState={model.stopeFillState} />
      )}
    </group>
  );
};

export default ModelRenderer;
